/**
 * Chain of Custody API service
 *
 * Provides functions for managing evidential chain of custody records.
 */

import { apiClient } from './api'
import type {
  ChainOfCustodyEntry,
  CreateChainOfCustodyEntryRequest,
  ChainVerification,
  ListChainOfCustodyQuery,
} from '../types/case-container'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// CHAIN OF CUSTODY API
// =============================================================================

/**
 * List chain of custody entries for a case
 */
export async function listChainOfCustody(
  caseId: string,
  params?: ListChainOfCustodyQuery
): Promise<ChainOfCustodyEntry[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<ChainOfCustodyEntry[]>(`/cases/${caseId}/chain-of-custody${query}`)
}

/**
 * Get chain of custody count for a case
 */
export async function countChainOfCustody(caseId: string): Promise<number> {
  return apiClient.get<number>(`/cases/${caseId}/chain-of-custody/count`)
}

/**
 * Get chain of custody history for a specific evidence item
 */
export async function getEvidenceHistory(
  caseId: string,
  evidenceType: string,
  evidenceId: string
): Promise<ChainOfCustodyEntry[]> {
  return apiClient.get<ChainOfCustodyEntry[]>(
    `/cases/${caseId}/chain-of-custody/evidence/${evidenceType}/${evidenceId}`
  )
}

/**
 * Create a chain of custody entry
 */
export async function createChainOfCustodyEntry(
  caseId: string,
  entry: CreateChainOfCustodyEntryRequest
): Promise<ChainOfCustodyEntry> {
  return apiClient.post<ChainOfCustodyEntry>(`/cases/${caseId}/chain-of-custody`, entry)
}

/**
 * Verify chain integrity for a case
 */
export async function verifyChain(caseId: string): Promise<ChainVerification> {
  return apiClient.get<ChainVerification>(`/cases/${caseId}/chain-of-custody/verify`)
}
